import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "date-picker-container border-radius" }
const _hoisted_2 = { class: "pt-3" }

import {reactive, computed, watch, inject, onMounted, nextTick} from 'vue';
import {useRouter, useRoute} from 'vue-router';
import {servicesService} from '@/api/services/service';
import {useSettingsStore} from '@/api/store/settings';
import {useUserStore} from '@/api/store/user';
import {useDate} from 'vuetify'
import BookingMethod from '@/components/custom/BookingMethod.vue';
import ButtonTab from '@/components/custom/ButtonTab.vue';
import TimePicker from '@/components/custom/TimePicker.vue';

import moment from 'moment';
import 'moment-timezone';
import PractitionerPage from "@/views/AppointmentsComponents/BookingComponents/BookingPages/PractitionerPage.vue";
import BookingHeader from "@/views/AppointmentsComponents/BookingComponents/BookingPages/BookingHeader.vue";
import DetailsPage from "@/views/AppointmentsComponents/BookingComponents/BookingPages/DetailsPage.vue";
import ButtonFixed from "@/components/custom/ButtonFixed.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'BookingCreate',
  setup(__props) {

const router = useRouter();
const route = useRoute();

const settingsStore = useSettingsStore();
const userStore = useUserStore();

const snackbar: any = inject('snackbar');

const date = useDate()

const state = reactive({
  service: null as any | null,
  tab: {name: "Practitioner"} as any,
  booking: {
    method: "" as string,
    date: moment(date).toDate() as any | null,
    time: null as any | null,
    practitioner: null as any | null,
    termsAgreed: true as boolean,
  },
  currentDate: moment(date).toDate(),
  availableDays: [] as any[],
  availableTimes: [] as any[],
  creatingBooking: false as boolean,
  pickerState: {
    currentYear: moment().year(),
    currentMonth: moment().month()
  }
});
// Methods
const loadService = async () => {
  await servicesService.getService(route.params.id, route.query.packageServiceId).then(response => {

    if (response.package) {

      let clinicService = response.package.services.filter((item) => {
        return route.params.id == item.clinic_service_id;
      })

      const firstServiceWithinPackage = clinicService[0];

      state.service = {
        ...firstServiceWithinPackage,
        package_name: response.package.name,
        booking_options: firstServiceWithinPackage.service?.booking_options,
        creator: firstServiceWithinPackage.service?.creator,
        price_display: `${response.package.currency.symbol}${response.package.price}`,
      };

    } else {
      state.service = response;
    }

    state.booking.method = firstAllowedMethod.value;
  });

  if (route.params.practitionerId) {
    state.booking.practitioner = state.service.creator;
    state.tab.name = 'Date/Method';
  }
};

const loadAvailableTimes = async () => {
  const date = state.booking.date;
  let serviceId = state.service.id;
  const method = state.booking.method;

  if (state.service?.service) {
    serviceId = state.service.service.id;
  }

  if (!date || !serviceId || !method) {
    snackbar.errorSnackbar("Please select a date, service, and method");
    return;
  }

  try {
    const formattedDate = moment(date).format('DD/MM/YY');
    const response = await servicesService.getServiceAvailability(serviceId, {
      date: formattedDate,
      method: method
    });

    state.availableDays = response.data;
    const formattedDateKey = moment(date).format('YYYY-MM-DD');
    if (state.availableDays[formattedDateKey]) {
      state.availableTimes = state.availableDays[formattedDateKey].slots;
      removeTodaysPastTimes(formattedDateKey);
    } else {
      state.availableTimes = [];
    }
  } catch (err) {
    console.error(err);
    snackbar.errorSnackbar("Failed to load availability");
  }
};

const removeTodaysPastTimes = (targetDate: any) =>{

  const currentDate = moment().format('YYYY-MM-DD');
  if (targetDate !== currentDate) return;

  console.log('removing past times');

  const currentTime = moment().format('HH:mm');
  state.availableTimes = state.availableTimes.filter(time => time > currentTime);
}

const allowedDates = (date) => {
  if (!state.availableDays) return false;

  // Format date to match backend format (YYYY-MM-DD)
  const formattedDate = moment(date).format('YYYY-MM-DD');

  // Check if the date exists in availableDays
  return state.availableDays[formattedDate] !== undefined;
};

const toggleSelectedPractitioner = () => {
  if (state.booking.practitioner === state.service.creator) {
    state.booking.practitioner = null;
  } else {
    state.booking.practitioner = state.service.creator;
    state.tab.name = 'Date/Method';
  }
};

// Watchers
watch(() => state.booking.date, (newDate) => {
  if (newDate) {
    const formattedDate = moment(newDate).format('YYYY-MM-DD');

    if (state.availableDays[formattedDate]) {
      state.availableTimes = state.availableDays[formattedDate].slots;
      removeTodaysPastTimes(formattedDate);
    }
  } else {
    state.availableTimes = []
  }
}, {immediate: true});

watch(() => state.booking.method, () => {
  if (state.booking.date) {
    loadAvailableTimes();
  }
});

watch(() => state.tab.name, () => {
  if (state.tab.name === 'Date/Method' || state.tab.name === 'Details') {
    nextTick(() => {
      const backArrow = document.querySelector('.back-arrow');

      setTimeout(() => {
        if (backArrow) {
          backArrow.style.display = 'none';
        }
      }, 100);
    });
  } else {
    nextTick(() => {
      const backArrow = document.querySelector('.back-arrow');

      setTimeout(() => {
        if (backArrow) {
          backArrow.style.display = 'block';
        }
      }, 100);
    });
  }
});

// Computed
/**
 * Returns the first allowed booking method for the service
 * and defaults to the first available method
 */
const firstAllowedMethod = computed(() => {
  let serviceOrPackageService = state.service;

  if (state.service?.service) {
    serviceOrPackageService = state.service.service;
  }

  if (serviceOrPackageService?.booking_options?.allow_standard && isServiceMethodAvailable.value.in_person) {
    return 'in_person';
  }
  if (serviceOrPackageService?.booking_options?.allow_standard && isServiceMethodAvailable.value.video) {
    return 'video';
  }
  if (serviceOrPackageService?.booking_options?.allow_standard && isServiceMethodAvailable.value.phone) {
    return 'phone';
  }

  return null;
})

// todo: check if this is refreshing correctly
const isServiceMethodAvailable = computed(() => {
  let serviceOrPackageService = state.service;

  if (state.service?.service) {
    serviceOrPackageService = state.service.service;
  }

  return {
    in_person: serviceOrPackageService.availability?.some(item => item.in_person == 1),
    video: serviceOrPackageService.availability?.some(item => item.video == 1),
    phone: serviceOrPackageService.availability?.some(item => item.phone == 1),
  }
});

const handleTimeNavigation = async (direction) => {
  const currentDate = state.booking.date;
  if (!currentDate) return;

  // Calculate the next/previous date based on direction
  const newDate = direction === 'next'
      ? moment(currentDate).add(1, 'day').toDate()
      : moment(currentDate).subtract(1, 'day').toDate();

  // Update the date and load new times
  state.booking.date = newDate;
  await loadAvailableTimes();
};

// Utility function
const loadAvailabilityForDate = async (date: moment.Moment) => {
  let serviceId = state.service?.service?.id || state.service?.id;

  if (!serviceId || !state.booking.method) {
    snackbar.errorSnackbar("Please select a service and method");
    return;
  }

  try {
    const formattedDate = date.format('DD/MM/YY');
    const response = await servicesService.getServiceAvailability(serviceId, {
      date: formattedDate,
      method: state.booking.method
    });

    state.availableDays = response.data;
    return response;
  } catch (err) {
    console.error('Availability load error:', err);
    snackbar.errorSnackbar("Failed to load availability");
  }
};

// Event handlers
const handleDateSelect = async (newDate) => {
  await loadAvailabilityForDate(moment(newDate));
};

const handleMonthChange = async (newMonth) => {
  // Keep the same year when changing months
  const currentYear = state.pickerState.currentYear;
  state.pickerState.currentMonth = newMonth;

  const selectedDate = moment()
      .year(currentYear)
      .month(newMonth)
      .startOf('month');

  await loadAvailabilityForDate(selectedDate);

  // Reset selections
  state.booking.date = null;
  state.booking.time = null;
  state.availableTimes = [];
};

const handleMethodChange = async (newValue) => {
  state.booking.method = newValue.method;

  if (state.booking.date) {
    await loadAvailableTimes();
  }
};

const handleYearChange = async (newYear) => {
  // Only called when explicitly changing year from year selector
  state.pickerState.currentYear = newYear;

  const selectedDate = moment()
      .year(newYear)
      .month(state.pickerState.currentMonth)
      .startOf('month');

  await loadAvailabilityForDate(selectedDate);

  // Reset selections
  state.booking.date = null;
  state.booking.time = null;
  state.availableTimes = [];
};

onMounted(() => {
  loadService();
});

return (_ctx: any,_cache: any) => {
  const _component_v_window_item = _resolveComponent("v-window-item")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_date_picker = _resolveComponent("v-date-picker")!
  const _component_v_window = _resolveComponent("v-window")!
  const _component_v_sheet = _resolveComponent("v-sheet")!
  const _component_v_container = _resolveComponent("v-container")!

  return (state.service)
    ? (_openBlock(), _createBlock(_component_v_container, {
        key: 0,
        class: "ma-0 pa-0 position-relative border-radius"
      }, {
        default: _withCtx(() => [
          _createVNode(BookingHeader, {
            booking: state.booking,
            service: state.service,
            "settings-store": _unref(settingsStore),
            tab: state.tab
          }, null, 8, ["booking", "service", "settings-store", "tab"]),
          _createVNode(ButtonTab, {
            modelValue: state.tab,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((state.tab) = $event)),
            colour: _unref(settingsStore).settings.primaryColor,
            dark: true,
            tabs: [
              { 
                name: 'Practitioner',
                icon: 'person-practitioner-icon',
                colour: 'white',
                disabled: false            
              }, 
              { 
                name: 'Date/Method',
                icon: 'calendar-icon',
                colour: 'white',
                disabled: !state.booking.practitioner          
              }, 
              {
                name: 'Details',
                icon: `${state.tab.name === 'Details'? 'info-icon-white' : 'info-icon'}`,
                colour: 'transparent',
                disabled: !state.booking.practitioner || !state.booking.date || !state.booking.time               
              }
          ],
            "bg-colour": "rgb(245, 245, 245)"
          }, null, 8, ["modelValue", "colour", "tabs"]),
          _createVNode(_component_v_sheet, {
            style: _normalizeStyle({backgroundColor: state.tab.name !== 'Details'? '#f5f5f5' : 'white'}),
            class: "pa-3",
            width: "100%"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_window, {
                modelValue: state.tab.name,
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((state.tab.name) = $event)),
                class: "pa-2",
                style: {"border-radius":"10px"}
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_window_item, { value: "Practitioner" }, {
                    default: _withCtx(() => [
                      _createVNode(PractitionerPage, {
                        "border-color": state.booking.practitioner ? _unref(settingsStore).settings.secondaryColor : '#F0F0F0',
                        "is-practitioner-selected": state.booking.practitioner === state.service.creator,
                        practitioner: state.service.creator,
                        onInfoSelected: _cache[1] || (_cache[1] = () => _unref(router).push({name: 'practitioner-bio', params:{ 
                serviceId: _unref(route).params.id,
                practitionerId: state.booking.practitioner?.id
              }})),
                        onSelect: toggleSelectedPractitioner
                      }, null, 8, ["border-color", "is-practitioner-selected", "practitioner"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_window_item, {
                    class: "mb-14",
                    value: "Date/Method"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(BookingMethod, {
                        modelValue: state.booking,
                        "onUpdate:modelValue": [
                          _cache[2] || (_cache[2] = ($event: any) => ((state.booking) = $event)),
                          handleMethodChange
                        ],
                        color: _unref(settingsStore).settings.secondaryColor,
                        "icon-color": _unref(settingsStore).settings.primaryColor,
                        "is-service-method-available": isServiceMethodAvailable.value
                      }, null, 8, ["modelValue", "color", "icon-color", "is-service-method-available"]),
                      _createVNode(_component_v_spacer, { class: "mt-2" }),
                      _createElementVNode("div", _hoisted_1, [
                        _createVNode(_component_v_date_picker, {
                          modelValue: state.booking.date,
                          "onUpdate:modelValue": [
                            _cache[3] || (_cache[3] = ($event: any) => ((state.booking.date) = $event)),
                            handleDateSelect
                          ],
                          "allowed-dates": allowedDates,
                          color: _unref(settingsStore).settings.secondaryColor,
                          "hide-actions": true,
                          min: state.currentDate,
                          style: {"width":"100%"},
                          "onUpdate:month": handleMonthChange,
                          "onUpdate:year": handleYearChange
                        }, null, 8, ["modelValue", "color", "min"]),
                        (state.availableTimes && state.availableTimes.length > 0)
                          ? (_openBlock(), _createBlock(TimePicker, {
                              key: 0,
                              modelValue: state.booking.time,
                              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((state.booking.time) = $event)),
                              availableTimeslots: state.availableTimes,
                              color: _unref(settingsStore).settings.secondaryColor,
                              highlightColor: _unref(settingsStore).settings.secondaryColor,
                              onNavigate: handleTimeNavigation
                            }, null, 8, ["modelValue", "availableTimeslots", "color", "highlightColor"]))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("section", _hoisted_2, [
                        _createVNode(ButtonFixed, {
                          disabled: !state.booking.date || !state.booking.time,
                          "custom-class": "text-white primary-button confirm-button",
                          text: "Confirm",
                          width: "100%",
                          onClick: _cache[5] || (_cache[5] = ($event: any) => (state.tab.name = 'Details'))
                        }, null, 8, ["disabled"])
                      ])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_window_item, {
                    style: {"background-color":"red"},
                    value: "Details"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(DetailsPage, {
                        "package-service-id": _unref(route).query.packageServiceId,
                        state: state,
                        "user-store": _unref(userStore)
                      }, null, 8, ["package-service-id", "state", "user-store"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]),
            _: 1
          }, 8, ["style"])
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})